"use client";

import React, { createContext, useContext, useEffect, useState } from "react";

type SidebarContextType = {
  isExpanded: boolean;
  isMobileOpen: boolean;
  isHovered: boolean;
  toggleSidebar: () => void;
  toggleMobileSidebar: () => void;
  setIsHovered: (v: boolean) => void;
};

const SidebarContext = createContext<SidebarContextType | null>(null);

export const useSidebar = () => {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebar must be used within SidebarProvider");
  return ctx;
};

export const SidebarProvider = ({ children }: { children: React.ReactNode }) => {
  const [mounted, setMounted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setMounted(true);

    // Mobile browsers fire `resize` whenever the address bar shows/hides while scrolling,
    // so only reset the sidebar when the mobile/desktop breakpoint is actually crossed —
    // otherwise scrolling the open sidebar's menu would close it.
    let wasMobile: boolean | null = null;
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      if (mobile === wasMobile) return;
      wasMobile = mobile;
      setIsMobile(mobile);
      setIsExpanded(!mobile);
      setIsMobileOpen(false);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const value: SidebarContextType = {
    isExpanded: mounted ? (!isMobile && isExpanded) : false,
    isMobileOpen: mounted ? isMobileOpen : false,
    isHovered: mounted ? isHovered : false,
    toggleSidebar: () => setIsExpanded((v) => !v),
    toggleMobileSidebar: () => setIsMobileOpen((v) => !v),
    setIsHovered,
  };

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};
